// Feb 2026 — Admin route map for the state-preserving tab shell.
//
// Each entry maps a static `/admin/*` pathname to:
//   - `title`: the label shown in the tab strip + browser title suffix
//   - `Component`: the lazy-loaded page component
//
// The list is authoritative — App.js consumes it via
// <AdminTabsShell/>, so adding a new admin page here also wires the
// route AND makes it tabbable in one step.
//
// Rules of thumb:
//   * Only STATIC admin paths. If you add a route with URL params
//     (e.g. `/admin/orders/:id`), open a separate <Route> outside the
//     shell — the tab shell keeps one mounted instance per path.
//   * Never register `/admin/login` here. Login is handled by a
//     separate <Route> so an unauthenticated user can't spawn a tab.

import AdminDashboard from '../pages/AdminDashboard';
import AdminProducts from '../pages/AdminProducts';
import AdminOrders from '../pages/AdminOrders';
import AdminCategories from '../pages/AdminCategories';
import AdminInquiries from '../pages/AdminInquiries';
import AdminCustomers from '../pages/AdminCustomers';
import AdminDrivers from '../pages/AdminDrivers';
import AdminDispatch from '../pages/AdminDispatch';
import AdminCredentials from '../pages/AdminCredentials';
import AdminUsers from '../pages/AdminUsers';
import AdminAttendance from '../pages/AdminAttendance';
import AdminMyWork from '../pages/AdminMyWork';
import AdminDeskTracker from '../pages/AdminDeskTracker';
import AdminWorkTracking from '../pages/AdminWorkTracking';
import SiteMap from '../pages/SiteMap';
import DataSecurity from '../pages/DataSecurity';
import AdminLockdown from '../pages/AdminLockdown';
import AdminSettings from '../pages/AdminSettings';
import AdminCutoffSettings from '../pages/AdminCutoffSettings';
import AdminBroadcast from '../pages/AdminBroadcast';
import EmailCampaigns from '../pages/EmailCampaigns';
import AdminStock from '../pages/AdminStock';
import AdminStockAdjust from '../pages/AdminStockAdjust';
import AdminAuditQueue from '../pages/AdminAuditQueue';
import AdminStockApprovals from '../pages/AdminStockApprovals';
import AdminDayClose from '../pages/AdminDayClose';
import AdminDiscrepancies from '../pages/AdminDiscrepancies';
import AdminLabelPrint from '../pages/AdminLabelPrint';
import AdminZones from '../pages/AdminZones';
import AdminNotificationsLog from '../pages/AdminNotificationsLog';
import AdminOutstanding from '../pages/AdminOutstanding';
import AdminPerformanceReport from '../pages/AdminPerformanceReport';
import AdminWarehouses from '../pages/AdminWarehouses';
import AdminBranches from '../pages/AdminBranches';
import AdminPallets from '../pages/AdminPallets';
import AdminGoodsReceiving from '../pages/AdminGoodsReceiving';
import AdminReleaseHoldAudit from '../pages/AdminReleaseHoldAudit';
import AdminBackupStockData from '../pages/AdminBackupStockData';
import AdminStockMismatch from '../pages/AdminStockMismatch';
import AdminStockReconciliation from '../pages/AdminStockReconciliation';
import AdminStockPosition from '../pages/AdminStockPosition';
import AdminAllocations from '../pages/AdminAllocations';
import AdminExportDashboard from '../pages/AdminExportDashboard';
import AccessActivity from '../pages/AccessActivity';
import AdminLayoutPage from '../pages/AdminLayoutPage';

export const ADMIN_ROUTE_MAP = {
  '/admin': { title: 'Dashboard', Component: AdminDashboard, pinned: true },
  '/admin/products': { title: 'Products', Component: AdminProducts },
  '/admin/orders': { title: 'Orders', Component: AdminOrders },
  '/admin/categories': { title: 'Divisions', Component: AdminCategories },
  '/admin/inquiries': { title: 'Inquiries', Component: AdminInquiries },
  '/admin/customers': { title: 'Customers', Component: AdminCustomers },
  '/admin/drivers': { title: 'Drivers', Component: AdminDrivers },
  '/admin/dispatch': { title: 'Dispatch', Component: AdminDispatch },
  '/admin/credentials': { title: 'Credentials', Component: AdminCredentials },
  '/admin/users': { title: 'Users', Component: AdminUsers },
  '/admin/attendance': { title: 'Attendance', Component: AdminAttendance },
  '/admin/my-work': { title: 'My Work', Component: AdminMyWork },
  '/admin/desk-tracker': { title: 'Desk Tracker', Component: AdminDeskTracker },
  '/admin/work-tracking': { title: 'Work Tracking', Component: AdminWorkTracking },
  '/admin/sitemap': { title: 'Sitemap', Component: SiteMap },
  '/admin/data-security': { title: 'Data Security', Component: DataSecurity },
  '/admin/lockdown': { title: 'Lockdown', Component: AdminLockdown },
  '/admin/settings': { title: 'Settings', Component: AdminSettings },
  '/admin/settings/cutoff': { title: 'Cutoff Settings', Component: AdminCutoffSettings },
  '/admin/broadcast': { title: 'Broadcast', Component: AdminBroadcast },
  '/admin/campaigns': { title: 'Email Campaigns', Component: EmailCampaigns },
  '/admin/stock': { title: 'Stock', Component: AdminStock },
  '/admin/stock-adjust': { title: 'Stock Adjustment', Component: AdminStockAdjust },
  '/admin/audit-queue': { title: 'Audit Queue', Component: AdminAuditQueue },
  '/admin/stock-approvals': { title: 'Stock Approvals', Component: AdminStockApprovals },
  '/admin/day-close': { title: 'Day Close', Component: AdminDayClose },
  '/admin/discrepancies': { title: 'Ledger Discrepancies', Component: AdminDiscrepancies },
  '/admin/label-print': { title: 'Label Printing', Component: AdminLabelPrint },
  '/admin/zones': { title: 'Zones', Component: AdminZones },
  '/admin/notifications-log': { title: 'Dispatch Log', Component: AdminNotificationsLog },
  '/admin/outstanding': { title: 'Outstanding', Component: AdminOutstanding },
  '/admin/performance-report': { title: 'Performance Report', Component: AdminPerformanceReport },
  '/admin/warehouses': { title: 'Warehouses', Component: AdminWarehouses },
  '/admin/branches': { title: 'Branches', Component: AdminBranches },
  '/admin/pallets': { title: 'Pallet Ledger', Component: AdminPallets },
  '/admin/goods-receiving': { title: 'Goods Receiving', Component: AdminGoodsReceiving },
  '/admin/release-hold-audit': { title: 'ASN Release-Hold Audit', Component: AdminReleaseHoldAudit },
  '/admin/backup-stock-data': { title: 'Backup Stock Data', Component: AdminBackupStockData },
  '/admin/stock-mismatch': { title: 'Stock Mismatch', Component: AdminStockMismatch },
  '/admin/stock-reconciliation': { title: 'Stock Reconciliation', Component: AdminStockReconciliation },
  '/admin/stock-position': { title: 'Stock Position', Component: AdminStockPosition },
  '/admin/allocations': { title: 'Allocation Details', Component: AdminAllocations },
  '/admin/export-dashboard': { title: 'Export Orders', Component: AdminExportDashboard },
  '/admin/access-activity': { title: 'Access Activity', Component: AccessActivity },
  '/admin/layout': { title: 'Layout Preview', Component: AdminLayoutPage },
};

export const isTabbableAdminPath = (pathname) => {
  const p = String(pathname || '').replace(/\/+$/, '') || '/admin';
  return Object.prototype.hasOwnProperty.call(ADMIN_ROUTE_MAP, p);
};

export const resolveAdminRoute = (pathname) => {
  const p = String(pathname || '').replace(/\/+$/, '') || '/admin';
  return ADMIN_ROUTE_MAP[p] || null;
};
